
import { Component, Vue, toNative, Prop } from 'vue-facing-decorator';
import moment from 'moment';
import { reactive } from 'vue';
import { minLength, required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import ValidationSummary from '@/components/Common/ValidationSummary.vue';
import ConfigureEmailsHelper from '@/helper/ConfigureEmailsHelper';
import EmailTemplatesTableModel from '@/entities/Invite/EmailTemplatesTableModel';
import Common from '@/helper/Common';
import { Root } from '@/main';
import { CustomEmailEditPanePopUpModal } from '@/entities/Invite/CustomEmailTableInputModel';

@Component({
  components : {
    ValidationSummary,
  },
})

@Component
class ConfigureEmailEditPaneModalPopUp extends Vue {
  // Props
  @Prop() private customTemplates!: EmailTemplatesTableModel[];
  @Prop() private customModalPopUp!: CustomEmailEditPanePopUpModal;

  // Icon images in the modal
  private tickIcon = require('@/assets/Images/Invite/blue-tick.svg');
  private crossIcon = require('@/assets/Images/Invite/modal-cross-icon.svg');

  private localModalPopUp: CustomEmailEditPanePopUpModal = CustomEmailEditPanePopUpModal.createEmpty();

  // Validation Summary flag
  private isValidationSummaryVisible = false;
  private validationErrorFields: string[] = [];

  // Local state
  private localInput = '';
  private selectedIndex: number | null = null;
  private selectedRow: EmailTemplatesTableModel | null = null;

  // Boolean flag for switching between input and table mode
  private isTableViewOn = false;
  // Max length for input field
  private maxTemplateNameLength = ConfigureEmailsHelper.MaxTemplateNameLength;

  private lastToastAt = ConfigureEmailsHelper.ToastLastAt;
  private toastCooldownMs = ConfigureEmailsHelper.ToastCoolDownMs;

  // Vue Validation states & rules logic
  private state = reactive({
    localInput: '',
  });

  private rules = {
    localInput: {
      required,
      minLength: minLength(1),
    },
  };

  private status(validation: { $error: any; $dirty: any }) {
    return {
      error: validation.$error,
      dirty: validation.$dirty,
    };
  }
  private v$ = useVuelidate(this.rules, this.state);

  // Mounted life cycle hook
  private mounted(){
    this.localModalPopUp = structuredClone(this.customModalPopUp);
    this.state.localInput = this.localInput;
    if(this.customTemplates.length > 0){
      this.isTableViewOn = true;
    }
  }

  // Function closing validation summary
  private CloseValidationSummary(visible: boolean) {
    this.isValidationSummaryVisible = visible;
  }

  // Function switching from Table mode to Input mode
  private ToggleToInputMode(){
    this.isTableViewOn = false;
    this.selectedRow = null;
    this.selectedIndex = null;
    this.validationErrorFields = [];
    this.isValidationSummaryVisible = false;
    this.localModalPopUp.Heading = this.$t('Invite.ConfigureEmails.ModalHeading.EnterTemplateName').toString();
  }

  // Function formating the date
  private FormatDate(date: string): string {
    return date ? moment(date).format('MMM D, YYYY, hh:mm A') : '';
  }


  // emit close event on closing modal
  private Cancel(): void {
    this.localInput = '';
    this.$emit('close');
    this.selectedRow = null;
    this.selectedIndex = null;
  }


  // Function checking whether the template name is unique or not
  private TemplateExist(input: string): boolean {
    if(!!this.customTemplates){
      const templateNameExist = this.customTemplates.some((template: EmailTemplatesTableModel) => (template.TemplateName).toLowerCase() === (input).toLowerCase());
      return templateNameExist;
    }
    return false;
  }

  // On Save button click
  private Submit(val: any): void {
    val.$touch();
    this.validationErrorFields = [];

    if(this.isTableViewOn && !this.localModalPopUp.IsMergeTagsOpen && (this.selectedIndex === null || this.selectedIndex === undefined)){
      this.validationErrorFields.push(
        this.$t('Invite.ConfigureEmails.TemplateRequired').toString(),
      );
      this.isValidationSummaryVisible = true;
      return;
    }
    if(!this.isTableViewOn && !this.localModalPopUp.IsMergeTagsOpen && val.localInput!.$error === true){
      this.validationErrorFields.push(
        this.$t('Invite.ConfigureEmails.TemplatesName').toString(),
      );
      this.isValidationSummaryVisible = true;
      return;
    }
    if(!this.isTableViewOn && !this.localModalPopUp.IsMergeTagsOpen &&  this.TemplateExist(this.state.localInput)){
      this.validationErrorFields.push(
        this.$t('Invite.ConfigureEmails.TemplateNameWarning').toString(),
      );
      this.isValidationSummaryVisible = true;
      return;
    }
    if(this.localModalPopUp.IsMergeTagsOpen){
      this.Cancel();
    }else{
      this.$emit('submit', this.selectedRow, this.state.localInput);
    }
  }

  private HandleTagClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    // Check if clicked element is an icon
    if (!target.classList.contains('copy-icon')) {
      return;
    }
    // Grab parent li
    const li = target.closest('li');
    if (!li) {
      return;
    }
    // Extract only the tag text (first text node)
    const tagName = li.childNodes[0].textContent?.trim() || '';
    Common.copyPublicLinkToClipboard(tagName);
    const now = Date.now();
    if (now - this.lastToastAt < this.toastCooldownMs) {
      return;
    }
    this.lastToastAt = now;
    Root.emit('show-toaster', tagName);
  }

  // Methods
  private SelectRow(idx: number, row: EmailTemplatesTableModel) {
    this.selectedIndex = idx;
    this.selectedRow = row;
  }
}

export default toNative(ConfigureEmailEditPaneModalPopUp);
